import React, { createContext, useContext, useState, ReactNode, useEffect, useRef } from 'react';
import {
  ChatSession,
  SearchResult,
  Prompt,
  SqlSearchCatalogResponse,
  SqlSearchExecuteResponse,
} from '../services/api';
import { FilterCondition } from '../services/api';
import type { SearchResultFullVideoState } from '../features/search-workspace/types';
import { persistentCache } from '../services/persistentCache';
import { DEFAULT_SQL_EDITOR_QUERY, DEFAULT_SQL_VIEW_MODE, SqlSearchViewMode } from '../features/sql-search/constants';
import { buildSqlSearchResultsFromExecuteResponse } from '../features/sql-search/rowMapping';
import {
  buildSearchResultIdentitySignature,
  getSearchResultIdentity,
} from '../utils/searchResultIdentity';
import {
  DEFAULT_CONDITION_FILTER,
  isConditionFilterSupported,
  normalizeConditionFilters,
  restoreConditionFilters,
} from '../utils/conditionSearchState';
import {
  PromptCreationDraft,
  normalizePromptCreationDraft,
} from '../features/prompt-creation/draft';
import { sanitizeMediaRecordForPersistence } from '../utils/mediaSources';
import type { PendingIndexDeletionMap } from '../utils/indexDeletionState';
import type { PendingVideoDeletionMap } from '../utils/videoDeletionState';
import {
  DEFAULT_PLAYGROUND_STUDIO_WORKSPACE_STATE,
  normalizePlaygroundStudioWorkspaceState,
  PlaygroundStudioWorkspaceState,
} from '../features/playground/studioWorkspaceState';
import { PLAYGROUND_GENERATED_ENGINE_ID } from '../features/playground/engineSelection';
import { DEFAULT_STUDIO_SEARCH_STATE, normalizeStudioSearchState, StudioSearchState } from '../features/studio-search/types';
import {
  safeLocalStorageGet,
  safeLocalStorageRemove,
  safeLocalStorageSet,
} from '../utils/safeStorage';
import {
  AutomationDirtyDraftsByIndex,
  createDefaultExportAutomationDraft,
  createDefaultImportAutomationDraft,
  ExportAutomationDraft,
  ImportAutomationDraft,
} from '../utils/automationRuntime';

// Define the state types for each tab
interface VectorSearchState {
  query: string;
  searchImageToken: string | null;
  selectedIndexes: string[] | null;
  searchResults: SearchResult[];
  error: string | null;
  showIndexDropdown: boolean;
  indexSortBy: 'name' | 'date';
  indexSortAsc: boolean;
  showAllIndexes: boolean;
  selectedIndexPrompt: Prompt | null;
  showPromptDetails: boolean;
  showRunDropdown: boolean;
  selectedRunIds: string[] | null;
  hasSearched: boolean;
  filters: FilterCondition[];
  showFilterPanel: boolean;
  searchFields: string[] | null;
}

interface ConditionSearchState {
  selectedIndexes: string[] | null;
  searchResults: SearchResult[];
  error: string | null;
  showIndexDropdown: boolean;
  indexSortBy: 'name' | 'date';
  indexSortAsc: boolean;
  showAllIndexes: boolean;
  selectedIndexPrompt: Prompt | null;
  showPromptDetails: boolean;
  filters: FilterCondition[];
  showRunDropdown: boolean;
  selectedRunIds: string[] | null;
  hasSearched: boolean;
  nextPageToken: string | null;
  nextCursor?: string | null;
  showFilterPanel: boolean;
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  metadata?: any;
  segments?: SearchResult[]; // Add segments associated with this message
}

interface AgentState {
  state: 'idle' | 'running' | 'complete' | 'error';
  message: string;
  progress: number;
}

interface AgentTimelineEvent {
  id: string;
  type: 'turn_started' | 'model_step' | 'tool_call_started' | 'tool_call_result' | 'assistant_message' | 'turn_completed' | 'error';
  label: string;
  detail?: string;
  timestamp: string;
}

interface AgenticSearchState {
  selectedIndexes: string[] | null;
  showIndexDropdown: boolean;
  indexSortBy: 'name' | 'date';
  indexSortAsc: boolean;
  showAllIndexes: boolean;
  showRunDropdown: boolean;
  selectedRunIds: string[] | null;
  sessionId: string | null;
  messages: Message[];
  suggestedSegments: SearchResult[];
  isProcessing: boolean;
  currentAgentState: AgentState;
  eventTimeline: AgentTimelineEvent[];
  error: string | null;
  sessions: ChatSession[];
  sessionsSyncedAt: number | null;
}

interface SqlSearchState {
  query: string;
  assistantPrompt: string;
  catalog: SqlSearchCatalogResponse | null;
  catalogScopeSignature: string | null;
  catalogManagedQuery: string | null;
  results: SqlSearchExecuteResponse | null;
  searchResults: SearchResult[];
  error: string | null;
  hasSearched: boolean;
  viewMode: SqlSearchViewMode;
  scopeSignature: string | null;
}

interface SearchTabState {
  vectorSearch: VectorSearchState;
  conditionSearch: ConditionSearchState;
  agenticSearch: AgenticSearchState;
  sqlSearch: SqlSearchState;
  activeMode: 'vector' | 'condition' | 'agentic' | 'sql';
  selectedSearchResult: SearchResult | null;
  selectedResultFullVideoState: SearchResultFullVideoState | null;
}

interface IndexTabState {
  selectedIndexId: string | null;
  selectedPromptRunId: string | null;
  resultMode: 'all' | 'matches';
  sortBy: 'date' | 'name';
  sortAsc: boolean;
  showCreateModal: boolean;
  showAddVideoModal: boolean;
  newIndexName: string;
  videoSearchQuery: string;
  videoSortBy: 'date' | 'name' | 'status';
  videoSortAsc: boolean;
  pendingIndexDeletions?: PendingIndexDeletionMap;
  pendingVideoDeletions?: PendingVideoDeletionMap;
  ingestionHistory: any[];
  ingestionHistoryLastSeenAt: string | null;
}

export type PlaygroundJourneyStep = 'media' | 'engine' | 'studio';

export interface PlaygroundAttemptIdentity {
  signature: string;
  key: string;
}

export interface PlaygroundViewState {
  activeStep: PlaygroundJourneyStep | null;
  activeStudioVideoId: string | null;
  studioRunId: string | null;
  studioPlaybackTime: number;
  admittedRunByVideoId: Record<string, string>;
  pendingRunAttempt: PlaygroundAttemptIdentity | null;
  videosNextCursor: string | null;
  hasMoreVideos: boolean;
  loadedVideoCount: number;
  studioSelectorScrollTop: number;
  studioWorkspaceState: PlaygroundStudioWorkspaceState;
  studioSearchMode: 'studio' | 'results' | 'result';
  studioSearchState: StudioSearchState;
  scrollTop: number;
}

interface PlaygroundTabState {
  videos: any[];
  pendingVideoDeletions?: PendingVideoDeletionMap;
  uploadProgress: number;
  isUploading: boolean;
  error: string | null;
  selectedPrompt: string;
  showUploadModal: boolean;
  sortBy: 'date' | 'name' | 'status';
  sortAsc: boolean;
  searchQuery: string;
  selectedVideos: Set<string>;
  currentPage: number;
  // Additional fields for full persistence
  currentVideo: any | null;
  segments: any[];
  selectedSegment: any | null;
  videoUrl: string;
  // Segments cache
  segmentsCache: { [videoId: string]: { segments: any[], timestamp: number } };
  // Serializable route/view state. Server records and signed URLs stay outside
  // this object so the Playground can be reconstructed safely after refresh.
  viewState: PlaygroundViewState;
}

export interface EnginesTabState {
  promptCreateDraft: PromptCreationDraft | null;
}

export type AutomationsView = 'import' | 'export';

export interface AutomationsTabState {
  selectedIndexId: string | null;
  activeView: AutomationsView;
  /** Auth-scoped editable drafts keyed by their owning index. */
  dirtyDraftsByIndex: AutomationDirtyDraftsByIndex;
  /** Derived from dirtyDraftsByIndex; never trusted as an independent state. */
  hasUnsavedChanges: boolean;
}

interface TabStateContextType {
  searchTabState: SearchTabState;
  setSearchTabState: React.Dispatch<React.SetStateAction<SearchTabState>>;
  indexTabState: IndexTabState;
  setIndexTabState: React.Dispatch<React.SetStateAction<IndexTabState>>;
  playgroundTabState: PlaygroundTabState;
  setPlaygroundTabState: React.Dispatch<React.SetStateAction<PlaygroundTabState>>;
  commitPlaygroundViewState: (patch: Partial<PlaygroundViewState>) => void;
  enginesTabState: EnginesTabState;
  setEnginesTabState: React.Dispatch<React.SetStateAction<EnginesTabState>>;
  automationsTabState: AutomationsTabState;
  setAutomationsTabState: React.Dispatch<React.SetStateAction<AutomationsTabState>>;
  playgroundTabStateReady: boolean;
  enginesTabStateReady: boolean;
  automationsTabStateReady: boolean;
  resetTabState: (tab: 'search' | 'index' | 'playground' | 'engines' | 'automations') => Promise<void>;
}

const defaultVectorSearchState: VectorSearchState = {
  query: '',
  searchImageToken: null,
  selectedIndexes: null,
  searchResults: [],
  error: null,
  showIndexDropdown: false,
  indexSortBy: 'date',
  indexSortAsc: false,
  showAllIndexes: false,
  selectedIndexPrompt: null,
  showPromptDetails: false,
  showRunDropdown: false,
  selectedRunIds: null,
  hasSearched: false,
  filters: [],
  showFilterPanel: false,
  searchFields: null,
};

const defaultConditionSearchState: ConditionSearchState = {
  selectedIndexes: null,
  searchResults: [],
  error: null,
  showIndexDropdown: false,
  indexSortBy: 'date',
  indexSortAsc: false,
  showAllIndexes: false,
  selectedIndexPrompt: null,
  showPromptDetails: false,
  filters: [{ ...DEFAULT_CONDITION_FILTER }],
  showRunDropdown: false,
  selectedRunIds: null,
  hasSearched: false,
  nextPageToken: null,
  nextCursor: null,
  showFilterPanel: true,
};

const defaultAgenticSearchState: AgenticSearchState = {
  selectedIndexes: null,
  showIndexDropdown: false,
  indexSortBy: 'date',
  indexSortAsc: false,
  showAllIndexes: false,
  showRunDropdown: false,
  selectedRunIds: null,
  sessionId: null,
  messages: [],
  suggestedSegments: [],
  isProcessing: false,
  currentAgentState: {
    state: 'idle',
    message: '',
    progress: 0,
  },
  eventTimeline: [],
  error: null,
  sessions: [],
  sessionsSyncedAt: null,
};

const defaultSqlSearchState: SqlSearchState = {
  query: DEFAULT_SQL_EDITOR_QUERY,
  assistantPrompt: '',
  catalog: null,
  catalogScopeSignature: null,
  catalogManagedQuery: DEFAULT_SQL_EDITOR_QUERY,
  results: null,
  searchResults: [],
  error: null,
  hasSearched: false,
  viewMode: DEFAULT_SQL_VIEW_MODE,
  scopeSignature: null,
};

const defaultSearchTabState: SearchTabState = {
  vectorSearch: defaultVectorSearchState,
  conditionSearch: defaultConditionSearchState,
  agenticSearch: defaultAgenticSearchState,
  sqlSearch: defaultSqlSearchState,
  activeMode: 'vector',
  selectedSearchResult: null,
  selectedResultFullVideoState: null,
};

const defaultIndexTabState: IndexTabState = {
  selectedIndexId: null,
  selectedPromptRunId: null,
  resultMode: 'all',
  sortBy: 'date',
  sortAsc: false,
  showCreateModal: false,
  showAddVideoModal: false,
  newIndexName: '',
  videoSearchQuery: '',
  videoSortBy: 'date',
  videoSortAsc: false,
  pendingIndexDeletions: {},
  pendingVideoDeletions: {},
  ingestionHistory: [],
  ingestionHistoryLastSeenAt: null,
};

export const DEFAULT_PLAYGROUND_VIEW_STATE: PlaygroundViewState = {
  activeStep: 'media',
  activeStudioVideoId: null,
  studioRunId: null,
  studioPlaybackTime: 0,
  admittedRunByVideoId: {},
  pendingRunAttempt: null,
  videosNextCursor: null,
  hasMoreVideos: false,
  loadedVideoCount: 0,
  studioSelectorScrollTop: 0,
  studioWorkspaceState: DEFAULT_PLAYGROUND_STUDIO_WORKSPACE_STATE,
  studioSearchMode: 'studio',
  studioSearchState: DEFAULT_STUDIO_SEARCH_STATE,
  scrollTop: 0,
};

const defaultPlaygroundTabState: PlaygroundTabState = {
  videos: [],
  pendingVideoDeletions: {},
  uploadProgress: 0,
  isUploading: false,
  error: null,
  selectedPrompt: '',
  showUploadModal: false,
  sortBy: 'date',
  sortAsc: false,
  searchQuery: '',
  selectedVideos: new Set(),
  currentPage: 1,
  // Additional fields for full persistence
  currentVideo: null,
  segments: [],
  selectedSegment: null,
  videoUrl: '',
  segmentsCache: {},
  viewState: DEFAULT_PLAYGROUND_VIEW_STATE,
};

const defaultEnginesTabState: EnginesTabState = {
  promptCreateDraft: null,
};

const defaultAutomationsTabState: AutomationsTabState = {
  selectedIndexId: null,
  activeView: 'import',
  dirtyDraftsByIndex: {},
  hasUnsavedChanges: false,
};

const TabStateContext = createContext<TabStateContextType | undefined>(undefined);

// Helper to serialize Set objects for localStorage
const serializeState = (state: any) => {
  const serialized = { ...state };
  if (state.selectedVideos instanceof Set) {
    serialized.selectedVideos = Array.from(state.selectedVideos);
  }
  return JSON.stringify(serialized);
};

// Helper to deserialize Set objects from localStorage
const deserializeState = (json: string, defaultState: any) => {
  try {
    const parsed = JSON.parse(json);
    // Restore Set objects
    if (Array.isArray(parsed.selectedVideos)) {
      parsed.selectedVideos = new Set(parsed.selectedVideos);
    }
    return { ...defaultState, ...parsed };
  } catch {
    return defaultState;
  }
};

const isSearchResultLike = (value: any): value is SearchResult => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const hasVideoId = typeof value.video_id === 'string' && value.video_id.trim().length > 0;
  const hasIdentity =
    (typeof value.result_id === 'string' && value.result_id.trim().length > 0)
    || (typeof value.segment_id === 'string' && value.segment_id.trim().length > 0)
    || (typeof value.preview_segment_id === 'string' && value.preview_segment_id.trim().length > 0);

  return hasVideoId && hasIdentity;
};

export const buildSearchMediaHydrationSignature = (
  state: Partial<SearchTabState> | null | undefined,
): string => {
  const vectorResults = state?.vectorSearch?.searchResults || [];
  const conditionResults = state?.conditionSearch?.searchResults || [];
  const sqlResults = state?.sqlSearch?.searchResults || [];
  const selectedIdentity = state?.selectedSearchResult
    ? getSearchResultIdentity(state.selectedSearchResult)
    : '';

  return [
    buildSearchResultIdentitySignature(vectorResults),
    buildSearchResultIdentitySignature(conditionResults),
    buildSearchResultIdentitySignature(sqlResults),
    selectedIdentity,
  ].join('::');
};

export const normalizeSearchTabState = (
  state: Partial<SearchTabState> | null | undefined,
): SearchTabState => {
  const parsed = state || {};
  const parsedVectorSearch: Partial<VectorSearchState> = parsed.vectorSearch || {};
  const parsedConditionSearch: Partial<ConditionSearchState> = parsed.conditionSearch || {};
  const parsedAgenticSearch: Partial<AgenticSearchState> = parsed.agenticSearch || {};
  const parsedSqlSearch: Partial<SqlSearchState> = parsed.sqlSearch || {};
  const normalizedVectorFilters = normalizeConditionFilters(parsedVectorSearch.filters);
  const normalizedConditionFilters = restoreConditionFilters(parsedConditionSearch.filters);
  const normalizedSqlViewMode: SqlSearchViewMode = parsedSqlSearch.viewMode === 'tile'
    ? 'tile'
    : DEFAULT_SQL_VIEW_MODE;
  const normalizedSqlResults: SqlSearchExecuteResponse | null = parsedSqlSearch.results
    && typeof parsedSqlSearch.results === 'object'
      ? {
          columns: Array.isArray(parsedSqlSearch.results.columns)
            ? parsedSqlSearch.results.columns.filter((column): column is string => typeof column === 'string')
            : [],
          rows: Array.isArray(parsedSqlSearch.results.rows)
            ? parsedSqlSearch.results.rows
              .filter((row): row is Record<string, any> => Boolean(row) && typeof row === 'object')
              .map(row => sanitizeMediaRecordForPersistence(row))
            : [],
          row_context: Array.isArray(parsedSqlSearch.results.row_context)
            ? parsedSqlSearch.results.row_context.map((rowContext) => (
              rowContext && typeof rowContext === 'object'
                ? sanitizeMediaRecordForPersistence(
                  rowContext as Record<string, any>,
                )
                : null
            ))
            : [],
          row_count: Number(parsedSqlSearch.results.row_count || 0),
          truncated: Boolean(parsedSqlSearch.results.truncated),
          applied_result_limit: Number(parsedSqlSearch.results.applied_result_limit || 0),
          tables: Array.isArray(parsedSqlSearch.results.tables)
            ? parsedSqlSearch.results.tables.filter((table): table is string => typeof table === 'string')
            : [],
          index_ids: Array.isArray(parsedSqlSearch.results.index_ids)
            ? parsedSqlSearch.results.index_ids.filter((indexId): indexId is string => typeof indexId === 'string')
            : [],
          run_ids: Array.isArray(parsedSqlSearch.results.run_ids)
            ? parsedSqlSearch.results.run_ids.filter((runId): runId is string => typeof runId === 'string')
            : [],
        }
      : null;
  const normalizedSqlCatalog: SqlSearchCatalogResponse | null = parsedSqlSearch.catalog
    && typeof parsedSqlSearch.catalog === 'object'
      ? {
          tables: Array.isArray(parsedSqlSearch.catalog.tables)
            ? parsedSqlSearch.catalog.tables.filter((table): table is SqlSearchCatalogResponse['tables'][number] => (
              Boolean(table)
              && typeof table === 'object'
              && typeof (table as any).table_name === 'string'
              && typeof (table as any).run_id === 'string'
              && typeof (table as any).index_id === 'string'
            ))
            : [],
          limits: parsedSqlSearch.catalog.limits
            && typeof parsedSqlSearch.catalog.limits === 'object'
            ? {
                max_loaded_rows: Number((parsedSqlSearch.catalog.limits as any).max_loaded_rows || 50000),
                default_result_limit: Number((parsedSqlSearch.catalog.limits as any).default_result_limit || 200),
                max_result_limit: Number((parsedSqlSearch.catalog.limits as any).max_result_limit || 2000),
                query_timeout_seconds: Number((parsedSqlSearch.catalog.limits as any).query_timeout_seconds || 15),
              }
            : {
                max_loaded_rows: 50000,
                default_result_limit: 200,
                max_result_limit: 2000,
                query_timeout_seconds: 15,
              },
          index_ids: Array.isArray(parsedSqlSearch.catalog.index_ids)
            ? parsedSqlSearch.catalog.index_ids.filter((indexId): indexId is string => typeof indexId === 'string')
            : [],
          run_ids: Array.isArray(parsedSqlSearch.catalog.run_ids)
            ? parsedSqlSearch.catalog.run_ids.filter((runId): runId is string => typeof runId === 'string')
            : [],
        }
      : null;
  const normalizedPersistedSqlSearchResults = Array.isArray(parsedSqlSearch.searchResults)
    ? parsedSqlSearch.searchResults
      .filter(isSearchResultLike)
      .map(result => sanitizeMediaRecordForPersistence(result))
    : [];
  const derivedSqlSearchResults = normalizedSqlResults
    ? buildSqlSearchResultsFromExecuteResponse(normalizedSqlResults)
    : [];
  const normalized: SearchTabState = {
    ...defaultSearchTabState,
    selectedSearchResult: parsed.selectedSearchResult ?? null,
    selectedResultFullVideoState: parsed.selectedResultFullVideoState ?? null,
    // Developer SQL is currently not exposed. Migrate legacy/persisted SQL and
    // condition modes to the unified Search workspace without discarding data.
    activeMode: parsed.activeMode === 'agentic' ? 'agentic' : 'vector',
    vectorSearch: {
      ...defaultVectorSearchState,
      ...parsedVectorSearch,
      filters: normalizedVectorFilters.every(isConditionFilterSupported)
        ? normalizedVectorFilters
        : [],
      searchResults: Array.isArray(parsedVectorSearch.searchResults)
        ? parsedVectorSearch.searchResults
          .filter(isSearchResultLike)
          .map(result => sanitizeMediaRecordForPersistence(result))
        : [],
    },
    conditionSearch: {
      ...defaultConditionSearchState,
      ...parsedConditionSearch,
      filters: normalizedConditionFilters,
      searchResults: Array.isArray(parsedConditionSearch.searchResults)
        ? parsedConditionSearch.searchResults
          .filter(isSearchResultLike)
          .map(result => sanitizeMediaRecordForPersistence(result))
        : [],
      nextPageToken: parsedConditionSearch.nextPageToken
        ?? parsedConditionSearch.nextCursor
        ?? null,
      nextCursor: parsedConditionSearch.nextCursor
        ?? parsedConditionSearch.nextPageToken
        ?? null,
    },
    agenticSearch: {
      ...defaultAgenticSearchState,
      ...parsedAgenticSearch,
      messages: [],
      suggestedSegments: [],
      sessions: [],
      sessionsSyncedAt: null,
      isProcessing: false,
      currentAgentState: {
        state: 'idle',
        message: '',
        progress: 0,
      },
      eventTimeline: [],
      error: null,
    },
    sqlSearch: {
      ...defaultSqlSearchState,
      ...parsedSqlSearch,
      query: typeof parsedSqlSearch.query === 'string'
        ? parsedSqlSearch.query
        : defaultSqlSearchState.query,
      assistantPrompt: typeof parsedSqlSearch.assistantPrompt === 'string'
        ? parsedSqlSearch.assistantPrompt
        : defaultSqlSearchState.assistantPrompt,
      catalog: normalizedSqlCatalog,
      catalogScopeSignature: typeof parsedSqlSearch.catalogScopeSignature === 'string'
        ? parsedSqlSearch.catalogScopeSignature
        : null,
      catalogManagedQuery: typeof parsedSqlSearch.catalogManagedQuery === 'string'
        ? parsedSqlSearch.catalogManagedQuery
        : null,
      results: normalizedSqlResults,
      searchResults: normalizedPersistedSqlSearchResults.length > 0
        ? normalizedPersistedSqlSearchResults
        : derivedSqlSearchResults,
      error: typeof parsedSqlSearch.error === 'string' ? parsedSqlSearch.error : null,
      hasSearched: Boolean(parsedSqlSearch.hasSearched),
      viewMode: normalizedSqlViewMode,
      scopeSignature: typeof parsedSqlSearch.scopeSignature === 'string'
        ? parsedSqlSearch.scopeSignature
        : null,
    },
  };

  const allResults = [
    ...normalized.vectorSearch.searchResults,
    ...normalized.conditionSearch.searchResults,
    ...normalized.sqlSearch.searchResults,
  ];

  if (normalized.selectedSearchResult && isSearchResultLike(normalized.selectedSearchResult)) {
    const selectedResultIdentity = getSearchResultIdentity(normalized.selectedSearchResult);
    const hasSelectedResult = allResults.some(
      (result) => getSearchResultIdentity(result) === selectedResultIdentity,
    );

    if (!hasSelectedResult) {
      normalized.selectedSearchResult = null;
      normalized.selectedResultFullVideoState = null;
      return normalized;
    }

    if (
      normalized.selectedResultFullVideoState
      && normalized.selectedResultFullVideoState.resultIdentity !== selectedResultIdentity
    ) {
      normalized.selectedResultFullVideoState = null;
    }

    return normalized;
  }

  normalized.selectedSearchResult = null;
  normalized.selectedResultFullVideoState = null;
  return normalized;
};

export const normalizeSearchTabStateForPersistence = (state: SearchTabState): SearchTabState => {
  const normalized = normalizeSearchTabState(state);
  return {
    ...normalized,
    selectedSearchResult: null,
    selectedResultFullVideoState: null,
    vectorSearch: {
      ...normalized.vectorSearch,
      searchResults: [],
      selectedIndexPrompt: null,
      hasSearched: false,
      error: null,
    },
    conditionSearch: {
      ...normalized.conditionSearch,
      searchResults: [],
      selectedIndexPrompt: null,
      hasSearched: false,
      nextPageToken: null,
      nextCursor: null,
      error: null,
    },
    agenticSearch: {
      ...normalized.agenticSearch,
      messages: [],
      suggestedSegments: [],
      sessions: [],
      sessionsSyncedAt: null,
      isProcessing: false,
      currentAgentState: {
        state: 'idle',
        message: '',
        progress: 0,
      },
      eventTimeline: [],
      error: null,
    },
    sqlSearch: {
      ...normalized.sqlSearch,
      catalog: null,
      catalogScopeSignature: null,
      results: null,
      searchResults: [],
      hasSearched: false,
      scopeSignature: null,
      error: null,
    },
  };
};

const normalizeSearchTabStateForRestore = (
  state: Partial<SearchTabState> | null | undefined,
): SearchTabState => normalizeSearchTabStateForPersistence(normalizeSearchTabState(state));

export const normalizeIndexTabStateForPersistence = (state: IndexTabState): IndexTabState => ({
  selectedIndexId: state.selectedIndexId,
  selectedPromptRunId: state.selectedPromptRunId,
  resultMode: state.resultMode === 'matches' ? 'matches' : 'all',
  sortBy: state.sortBy,
  sortAsc: state.sortAsc,
  showCreateModal: false,
  showAddVideoModal: false,
  newIndexName: state.newIndexName,
  videoSearchQuery: state.videoSearchQuery,
  videoSortBy: state.videoSortBy,
  videoSortAsc: state.videoSortAsc,
  pendingIndexDeletions: state.pendingIndexDeletions || {},
  pendingVideoDeletions: state.pendingVideoDeletions || {},
  ingestionHistory: [],
  ingestionHistoryLastSeenAt: null,
});

// Clean search tab state before persisting to localStorage
const cleanSearchTabState = (state: SearchTabState): SearchTabState => {
  try {
    const normalizedState = normalizeSearchTabStateForPersistence(state);

    // Create a deep clone with circular reference handling
    const cleaned = JSON.parse(JSON.stringify(normalizedState, (key, value) => {
      // Skip functions and undefined values
      if (typeof value === 'function' || value === undefined) {
        return undefined;
      }
      // Handle Sets by converting to arrays
      if (value instanceof Set) {
        return Array.from(value);
      }
      // Skip very large strings (like base64 data)
      if (typeof value === 'string' && value.length > 10000) {
        return '[TRUNCATED]';
      }
      return value;
    }));

    // Search datasets and selected-result media are runtime-only. Persistence
    // keeps query/scope controls, never a renderable result tree.
    cleaned.vectorSearch.searchResults = [];
    cleaned.vectorSearch.hasSearched = false;
    cleaned.conditionSearch.searchResults = [];
    cleaned.conditionSearch.hasSearched = false;
    cleaned.conditionSearch.nextPageToken = null;
    cleaned.conditionSearch.nextCursor = null;
    cleaned.sqlSearch.results = null;
    cleaned.sqlSearch.searchResults = [];
    cleaned.sqlSearch.hasSearched = false;
    cleaned.sqlSearch.scopeSignature = null;
    cleaned.selectedSearchResult = null;
    cleaned.selectedResultFullVideoState = null;

    if (cleaned.agenticSearch) {
      cleaned.agenticSearch.messages = [];
      cleaned.agenticSearch.suggestedSegments = [];
      cleaned.agenticSearch.sessions = [];
      cleaned.agenticSearch.sessionsSyncedAt = null;
      cleaned.agenticSearch.isProcessing = false;
      cleaned.agenticSearch.currentAgentState = {
        state: 'idle',
        message: '',
        progress: 0,
      };
      cleaned.agenticSearch.eventTimeline = [];
      cleaned.agenticSearch.error = null;
    }

    return cleaned;
  } catch (error) {
    console.error('Failed to clean search tab state:', error);
    // Return a minimal safe state if cleaning fails
    return {
      ...state,
      selectedSearchResult: null,
      selectedResultFullVideoState: null,
      vectorSearch: {
        ...state.vectorSearch,
        searchResults: [],
        hasSearched: false,
        error: null,
      },
      conditionSearch: {
        ...state.conditionSearch,
        searchResults: [],
        hasSearched: false,
        nextPageToken: null,
        nextCursor: null,
        error: null,
      },
      sqlSearch: {
        ...state.sqlSearch,
        catalog: null,
        catalogScopeSignature: null,
        results: null,
        searchResults: [],
        hasSearched: false,
        scopeSignature: null,
        error: null,
      },
      agenticSearch: {
        ...state.agenticSearch,
        suggestedSegments: [],
        messages: [],
        sessions: [],
        sessionsSyncedAt: null,
        isProcessing: false,
        currentAgentState: {
          state: 'idle',
          message: '',
          progress: 0,
        },
        eventTimeline: [],
        error: null,
      }
    };
  }
};

const PLAYGROUND_STATE_SCHEMA_VERSION = 1;

const normalizeNullableString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const normalized = value.trim();
  return normalized || null;
};

const normalizePlaygroundViewState = (value: unknown, legacyState?: any): PlaygroundViewState => {
  const candidate = value && typeof value === 'object' ? value as Partial<PlaygroundViewState> : {};
  const activeStep: PlaygroundJourneyStep | null = candidate.activeStep === null ? null : (
    candidate.activeStep === 'engine' || (candidate.activeStep as string) === 'review'
  ) ? 'engine' : candidate.activeStep === 'studio' ? 'studio' : 'media';
  const rawPlaybackTime = Number(candidate.studioPlaybackTime);
  const rawScrollTop = Number(candidate.scrollTop);
  const rawLoadedVideoCount = Number(candidate.loadedVideoCount);
  const rawStudioSelectorScrollTop = Number(candidate.studioSelectorScrollTop);
  const admittedRunByVideoId = candidate.admittedRunByVideoId
    && typeof candidate.admittedRunByVideoId === 'object'
    ? Object.entries(candidate.admittedRunByVideoId).reduce<Record<string, string>>(
        (normalized, [videoId, runId]) => {
          const normalizedVideoId = normalizeNullableString(videoId);
          const normalizedRunId = normalizeNullableString(runId);
          if (normalizedVideoId && normalizedRunId) normalized[normalizedVideoId] = normalizedRunId;
          return normalized;
        },
        {},
      )
    : {};
  const pendingRunAttemptCandidate = candidate.pendingRunAttempt;
  const pendingRunAttempt = pendingRunAttemptCandidate
    && typeof pendingRunAttemptCandidate === 'object'
    && normalizeNullableString(pendingRunAttemptCandidate.signature)
    && normalizeNullableString(pendingRunAttemptCandidate.key)
    ? {
        signature: String(pendingRunAttemptCandidate.signature),
        key: String(pendingRunAttemptCandidate.key),
      }
    : null;

  return {
    activeStep,
    activeStudioVideoId: normalizeNullableString(
      candidate.activeStudioVideoId ?? legacyState?.currentVideoId,
    ),
    studioRunId: normalizeNullableString(candidate.studioRunId),
    studioPlaybackTime: Number.isFinite(rawPlaybackTime) && rawPlaybackTime >= 0
      ? rawPlaybackTime
      : 0,
    admittedRunByVideoId,
    pendingRunAttempt,
    videosNextCursor: normalizeNullableString(candidate.videosNextCursor),
    hasMoreVideos: Boolean(candidate.hasMoreVideos),
    loadedVideoCount: Number.isFinite(rawLoadedVideoCount) && rawLoadedVideoCount >= 0
      ? Math.min(5000, Math.round(rawLoadedVideoCount))
      : 0,
    studioSelectorScrollTop: Number.isFinite(rawStudioSelectorScrollTop)
      && rawStudioSelectorScrollTop >= 0
      ? rawStudioSelectorScrollTop
      : 0,
    studioWorkspaceState: normalizePlaygroundStudioWorkspaceState(
      candidate.studioWorkspaceState,
    ),
    studioSearchMode: candidate.studioSearchMode === 'results' || candidate.studioSearchMode === 'result'
      ? candidate.studioSearchMode : 'studio',
    studioSearchState: normalizeStudioSearchState(candidate.studioSearchState),
    scrollTop: Number.isFinite(rawScrollTop) && rawScrollTop >= 0 ? rawScrollTop : 0,
  };
};

const restorePlaygroundTabState = (stored: string): PlaygroundTabState => {
  const parsed: any = deserializeState(stored, {});
  const persistedSelectedPrompt = typeof parsed.selectedPrompt === 'string'
    ? parsed.selectedPrompt.trim()
    : '';
  const selectedPrompt = !persistedSelectedPrompt && parsed.viewState?.activeStep === 'review'
    ? PLAYGROUND_GENERATED_ENGINE_ID
    : persistedSelectedPrompt;
  const selectedVideos = parsed.selectedVideos instanceof Set
    ? new Set(Array.from(parsed.selectedVideos).filter((videoId): videoId is string => (
        typeof videoId === 'string' && videoId.trim().length > 0
      )))
    : new Set<string>();
  const pendingVideoDeletions = parsed.pendingVideoDeletions
    && typeof parsed.pendingVideoDeletions === 'object'
    ? parsed.pendingVideoDeletions
    : {};

  return {
    ...defaultPlaygroundTabState,
    // Keep the legacy projection readable for older consumers. Playground
    // itself owns this value through the normalized view state below.
    searchQuery: typeof parsed.searchQuery === 'string' ? parsed.searchQuery : '',
    selectedPrompt,
    selectedVideos,
    pendingVideoDeletions,
    viewState: normalizePlaygroundViewState(parsed.viewState, parsed),
  };
};

// Persist only serializable decisions and IDs. Prompt creation drafts belong
// exclusively to Engines, while server records, caches, and signed URLs reload
// from their existing authoritative sources.
const splitPlaygroundState = (state: PlaygroundTabState) => ({
  schemaVersion: PLAYGROUND_STATE_SCHEMA_VERSION,
  selectedPrompt: state.selectedPrompt,
  selectedVideos: state.selectedVideos instanceof Set
    ? new Set(state.selectedVideos)
    : new Set<string>(),
  pendingVideoDeletions: state.pendingVideoDeletions || {},
  viewState: normalizePlaygroundViewState(state.viewState),
});

const ENGINES_STATE_SCHEMA_VERSION = 1;

interface PersistedEnginesTabState {
  schemaVersion: typeof ENGINES_STATE_SCHEMA_VERSION;
  savedAt: number;
  promptCreateDraft: PromptCreationDraft | null;
}

const buildPersistedEnginesTabState = (
  state: EnginesTabState,
): PersistedEnginesTabState => ({
  schemaVersion: ENGINES_STATE_SCHEMA_VERSION,
  savedAt: Date.now(),
  promptCreateDraft: normalizePromptCreationDraft(state.promptCreateDraft),
});

const isPersistedEnginesTabState = (
  value: unknown,
): value is PersistedEnginesTabState => Boolean(
  value
  && typeof value === 'object'
  && (value as Partial<PersistedEnginesTabState>).schemaVersion === ENGINES_STATE_SCHEMA_VERSION,
);

const getTabStateStorageKey = (baseKey: string, userId: string | null): string => (
  userId ? `${baseKey}:${userId}` : baseKey
);

const TAB_STATE_TTL_MS = 24 * 60 * 60 * 1000;

const isFreshStoredState = (storedTime: string | null): boolean => {
  if (!storedTime) {
    return false;
  }

  const savedAt = Number.parseInt(storedTime, 10);
  return Number.isFinite(savedAt) && Date.now() - savedAt < TAB_STATE_TTL_MS;
};

const readFreshLocalState = (stateKey: string, stateTimeKey: string): string | null => {
  const stored = safeLocalStorageGet(stateKey);
  if (!stored) {
    return null;
  }

  return isFreshStoredState(safeLocalStorageGet(stateTimeKey)) ? stored : null;
};

const restoreSearchTabState = (stored: string): SearchTabState => {
  const parsed = deserializeState(stored, defaultSearchTabState);
  return normalizeSearchTabStateForRestore(parsed);
};

const loadSearchTabStateFromStorage = (
  shouldPersistState: boolean,
  stateKey: string,
  stateTimeKey: string
): SearchTabState => {
  if (!shouldPersistState) {
    return defaultSearchTabState;
  }

  const stored = readFreshLocalState(stateKey, stateTimeKey);
  return stored ? restoreSearchTabState(stored) : defaultSearchTabState;
};

const loadIndexTabStateFromStorage = (
  shouldPersistState: boolean,
  stateKey: string,
  stateTimeKey: string
): IndexTabState => {
  if (!shouldPersistState) {
    return defaultIndexTabState;
  }

  const stored = readFreshLocalState(stateKey, stateTimeKey);
  if (!stored) {
    return defaultIndexTabState;
  }

  const state = deserializeState(stored, defaultIndexTabState);
  return normalizeIndexTabStateForPersistence(state);
};

const isRecord = (value: unknown): value is Record<string, unknown> => Boolean(
  value && typeof value === 'object' && !Array.isArray(value),
);

const normalizePersistedImportAutomationDraft = (
  value: unknown,
): ImportAutomationDraft | null => {
  if (!isRecord(value)) return null;
  const defaults = createDefaultImportAutomationDraft();
  const nullableString = (
    candidate: unknown,
    fallback: string | null,
  ): string | null => (
    typeof candidate === 'string' || candidate === null ? candidate : fallback
  );
  return {
    enabled: typeof value.enabled === 'boolean' ? value.enabled : defaults.enabled,
    paused: typeof value.paused === 'boolean' ? value.paused : defaults.paused,
    connector_id: typeof value.connector_id === 'string'
      ? value.connector_id
      : defaults.connector_id,
    source_prefix: typeof value.source_prefix === 'string'
      ? value.source_prefix
      : defaults.source_prefix,
    file_pattern: typeof value.file_pattern === 'string'
      ? value.file_pattern
      : defaults.file_pattern,
    recursive: typeof value.recursive === 'boolean' ? value.recursive : defaults.recursive,
    debounce_interval: nullableString(value.debounce_interval, defaults.debounce_interval),
    prompt_id: typeof value.prompt_id === 'string' ? value.prompt_id : defaults.prompt_id,
    runtime_status: null,
    runtime_error: null,
  };
};

const normalizePersistedExportAutomationDraft = (
  value: unknown,
): ExportAutomationDraft | null => {
  if (!isRecord(value)) return null;
  const defaults = createDefaultExportAutomationDraft();
  return {
    enabled: typeof value.enabled === 'boolean' ? value.enabled : defaults.enabled,
    paused: typeof value.paused === 'boolean' ? value.paused : defaults.paused,
    destination_connector_id: typeof value.destination_connector_id === 'string'
      ? value.destination_connector_id
      : defaults.destination_connector_id,
    destination_subpath: typeof value.destination_subpath === 'string'
      ? value.destination_subpath
      : defaults.destination_subpath,
    runtime_status: null,
    runtime_error: null,
  };
};

const normalizeAutomationDirtyDraftsByIndex = (
  value: unknown,
): AutomationDirtyDraftsByIndex => {
  if (!isRecord(value)) return {};
  return Object.entries(value).reduce<AutomationDirtyDraftsByIndex>((result, [rawIndexId, entry]) => {
    const indexId = rawIndexId.trim();
    if (!indexId || !isRecord(entry)) return result;
    const importDraft = normalizePersistedImportAutomationDraft(entry.importDraft);
    const exportDraft = normalizePersistedExportAutomationDraft(entry.exportDraft);
    if (!importDraft && !exportDraft) return result;
    result[indexId] = { importDraft, exportDraft };
    return result;
  }, {});
};

const normalizeAutomationsTabState = (
  state: Partial<AutomationsTabState> | null | undefined,
): AutomationsTabState => {
  const selectedIndexId = typeof state?.selectedIndexId === 'string'
    ? state.selectedIndexId.trim() || null
    : null;
  const dirtyDraftsByIndex = normalizeAutomationDirtyDraftsByIndex(
    state?.dirtyDraftsByIndex,
  );
  return {
    selectedIndexId,
    activeView: state?.activeView === 'export' ? 'export' : 'import',
    dirtyDraftsByIndex,
    hasUnsavedChanges: Object.keys(dirtyDraftsByIndex).length > 0,
  };
};

const buildPersistedAutomationsTabState = (
  state: AutomationsTabState,
): Pick<AutomationsTabState, 'selectedIndexId' | 'activeView' | 'dirtyDraftsByIndex'> => {
  const normalized = normalizeAutomationsTabState(state);
  return {
    selectedIndexId: normalized.selectedIndexId,
    activeView: normalized.activeView,
    dirtyDraftsByIndex: normalized.dirtyDraftsByIndex,
  };
};

const loadAutomationsTabStateFromStorage = (
  shouldPersistState: boolean,
  stateKey: string,
  stateTimeKey: string,
): AutomationsTabState => {
  if (!shouldPersistState) {
    return defaultAutomationsTabState;
  }

  const stored = readFreshLocalState(stateKey, stateTimeKey);
  if (!stored) {
    return defaultAutomationsTabState;
  }

  return normalizeAutomationsTabState(
    deserializeState(stored, defaultAutomationsTabState),
  );
};

const loadPlaygroundTabStateFromStorage = (
  shouldPersistState: boolean,
  stateKey: string,
  stateTimeKey: string,
): PlaygroundTabState => {
  if (!shouldPersistState) {
    return defaultPlaygroundTabState;
  }

  const stored = readFreshLocalState(stateKey, stateTimeKey);
  return stored ? restorePlaygroundTabState(stored) : defaultPlaygroundTabState;
};

export const TabStateProvider: React.FC<{ children: ReactNode; userId?: string | null }> = ({
  children,
  userId,
}) => {
  const hasExplicitAuthScope = userId !== undefined;
  const storageUserId = userId || null;
  const persistenceScopeId = storageUserId || '__anonymous__';
  const persistenceWriteScope = hasExplicitAuthScope ? storageUserId : undefined;
  const shouldPersistState = !hasExplicitAuthScope || Boolean(storageUserId);
  const searchStateKey = getTabStateStorageKey('searchTabState', storageUserId);
  const searchStateTimeKey = getTabStateStorageKey('searchTabState_time', storageUserId);
  const indexStateKey = getTabStateStorageKey('indexTabState', storageUserId);
  const indexStateTimeKey = getTabStateStorageKey('indexTabState_time', storageUserId);
  const playgroundStateKey = getTabStateStorageKey('playgroundTabState', storageUserId);
  const playgroundStateTimeKey = getTabStateStorageKey('playgroundTabState_time', storageUserId);
  const playgroundHeavyDataKey = getTabStateStorageKey('playgroundHeavyData', storageUserId);
  const enginesStateKey = getTabStateStorageKey('enginesTabState', storageUserId);
  const automationsStateKey = getTabStateStorageKey('automationsTabState', storageUserId);
  const automationsStateTimeKey = getTabStateStorageKey('automationsTabState_time', storageUserId);

  // Initialize states from localStorage with TTL check
  const [searchTabState, setSearchTabState] = useState<SearchTabState>(() => {
    return loadSearchTabStateFromStorage(shouldPersistState, searchStateKey, searchStateTimeKey);
  });

  const lastSavedSearchStateRef = useRef<string | null>(null);
  const [hydratedStorageUserId, setHydratedStorageUserId] = useState<string | null>(storageUserId);
  const isHydratedForCurrentScope = hydratedStorageUserId === storageUserId;

  React.useLayoutEffect(() => {
    if (hasExplicitAuthScope) {
      persistentCache.activateUserScope(storageUserId);
    }
  }, [hasExplicitAuthScope, storageUserId]);

  const [indexTabState, setIndexTabState] = useState<IndexTabState>(() => {
    return loadIndexTabStateFromStorage(shouldPersistState, indexStateKey, indexStateTimeKey);
  });

  const [automationsTabState, setAutomationsTabStateValue] = useState<AutomationsTabState>(() => (
    loadAutomationsTabStateFromStorage(
      shouldPersistState,
      automationsStateKey,
      automationsStateTimeKey,
    )
  ));
  const automationsTabStateRef = useRef(automationsTabState);
  const setAutomationsTabState = React.useCallback<
    React.Dispatch<React.SetStateAction<AutomationsTabState>>
  >((nextState) => {
    const current = automationsTabStateRef.current;
    const next = typeof nextState === 'function'
      ? (nextState as (previous: AutomationsTabState) => AutomationsTabState)(current)
      : nextState;
    const normalized = normalizeAutomationsTabState(next);
    automationsTabStateRef.current = normalized;
    setAutomationsTabStateValue(normalized);
  }, []);

  const [playgroundTabState, setPlaygroundTabStateValue] = useState<PlaygroundTabState>(() => (
    loadPlaygroundTabStateFromStorage(
      shouldPersistState,
      playgroundStateKey,
      playgroundStateTimeKey,
    )
  ));
  const [playgroundInitialized, setPlaygroundInitialized] = useState(true);
  const lastSavedPlaygroundStateRef = useRef<string | null>(null);
  const playgroundTabStateRef = useRef(playgroundTabState);
  const setPlaygroundTabState = React.useCallback<
    React.Dispatch<React.SetStateAction<PlaygroundTabState>>
  >((nextState) => {
    const current = playgroundTabStateRef.current;
    const next = typeof nextState === 'function'
      ? (nextState as (previous: PlaygroundTabState) => PlaygroundTabState)(current)
      : nextState;
    playgroundTabStateRef.current = next;
    setPlaygroundTabStateValue(next);
  }, []);
  const [enginesTabState, setEnginesTabStateValue] = useState<EnginesTabState>(defaultEnginesTabState);
  const [enginesInitialized, setEnginesInitialized] = useState(false);
  const enginesStateMutationRevisionRef = useRef(0);
  const setEnginesTabState = React.useCallback<
    React.Dispatch<React.SetStateAction<EnginesTabState>>
  >((nextState) => {
    enginesStateMutationRevisionRef.current += 1;
    setEnginesTabStateValue(nextState);
  }, []);

  useEffect(() => {
    if (!hasExplicitAuthScope) {
      return;
    }

    if (hydratedStorageUserId === storageUserId) {
      return;
    }

    lastSavedSearchStateRef.current = null;

    if (shouldPersistState) {
      setSearchTabState(loadSearchTabStateFromStorage(true, searchStateKey, searchStateTimeKey));
      setIndexTabState(loadIndexTabStateFromStorage(true, indexStateKey, indexStateTimeKey));
      setAutomationsTabState(loadAutomationsTabStateFromStorage(
        true,
        automationsStateKey,
        automationsStateTimeKey,
      ));
      setPlaygroundTabState(loadPlaygroundTabStateFromStorage(
        true,
        playgroundStateKey,
        playgroundStateTimeKey,
      ));
    } else {
      setSearchTabState(defaultSearchTabState);
      setIndexTabState(defaultIndexTabState);
      setAutomationsTabState(defaultAutomationsTabState);
      setPlaygroundTabState(defaultPlaygroundTabState);
    }

    lastSavedPlaygroundStateRef.current = null;
    setPlaygroundInitialized(true);
    enginesStateMutationRevisionRef.current += 1;
    setEnginesTabStateValue(defaultEnginesTabState);
    setEnginesInitialized(false);
    setHydratedStorageUserId(storageUserId);
  }, [
    hasExplicitAuthScope,
    hydratedStorageUserId,
    automationsStateKey,
    automationsStateTimeKey,
    indexStateKey,
    indexStateTimeKey,
    playgroundStateKey,
    playgroundStateTimeKey,
    searchStateKey,
    searchStateTimeKey,
    setAutomationsTabState,
    setPlaygroundTabState,
    shouldPersistState,
    storageUserId,
  ]);

  // Hydrate the Engines draft and migrate the former Playground-owned draft
  // once. A versioned empty Engines payload is a deliberate value and prevents
  // a cleared draft from being resurrected by stale legacy metadata.
  useEffect(() => {
    if (!isHydratedForCurrentScope) {
      return;
    }

    if (!shouldPersistState) {
      setEnginesInitialized(true);
      return;
    }

    let isCancelled = false;
    const hydrationMutationRevision = enginesStateMutationRevisionRef.current;
    const writeEpoch = persistentCache.captureWriteEpoch();
    const isCurrentPersistenceScope = () => persistentCache.isWriteAllowed(
      persistenceWriteScope,
      writeEpoch,
    );
    const canApplyHydration = () => (
      !isCancelled
      && enginesStateMutationRevisionRef.current === hydrationMutationRevision
      && isCurrentPersistenceScope()
    );

    const loadEnginesState = async () => {
      try {
        const persistedState = await persistentCache.getMetadata(enginesStateKey);
        if (!canApplyHydration()) {
          return;
        }

        if (isPersistedEnginesTabState(persistedState)) {
          const isFresh = Number.isFinite(persistedState.savedAt)
            && Date.now() - persistedState.savedAt < TAB_STATE_TTL_MS;
          setEnginesTabStateValue({
            promptCreateDraft: isFresh
              ? normalizePromptCreationDraft(persistedState.promptCreateDraft)
              : null,
          });
          return;
        }

        const legacyStateIsFresh = isFreshStoredState(
          safeLocalStorageGet(playgroundStateTimeKey),
        );
        const legacyState = legacyStateIsFresh
          ? await persistentCache.getMetadata(playgroundHeavyDataKey)
          : null;
        if (!canApplyHydration()) {
          return;
        }

        const migratedDraft = normalizePromptCreationDraft(legacyState?.promptCreateDraft);
        const migratedState: EnginesTabState = {
          promptCreateDraft: migratedDraft,
        };

        // Establish the versioned destination before clearing the legacy key.
        if (!canApplyHydration()) {
          return;
        }
        await persistentCache.setMetadata(
          enginesStateKey,
          buildPersistedEnginesTabState(migratedState),
          persistenceWriteScope,
        );
        if (migratedDraft && canApplyHydration()) {
          await persistentCache.setMetadata(
            playgroundHeavyDataKey,
            null,
            persistenceWriteScope,
          );
        }

        if (canApplyHydration()) {
          setEnginesTabStateValue(migratedState);
        }
      } catch (error) {
        console.warn('Failed to load extraction engine state:', error);
        if (canApplyHydration()) {
          setEnginesTabStateValue(defaultEnginesTabState);
        }
      } finally {
        if (!isCancelled) {
          setEnginesInitialized(true);
        }
      }
    };

    void loadEnginesState();

    return () => {
      isCancelled = true;
    };
  }, [
    enginesStateKey,
    isHydratedForCurrentScope,
    persistenceWriteScope,
    playgroundHeavyDataKey,
    playgroundStateTimeKey,
    shouldPersistState,
  ]);

  // Persist states to localStorage whenever they change
  const serializedPlaygroundState = serializeState(splitPlaygroundState(playgroundTabState));
  const serializedPlaygroundStateRef = useRef(serializedPlaygroundState);
  serializedPlaygroundStateRef.current = serializedPlaygroundState;

  const commitPlaygroundViewState = React.useCallback((patch: Partial<PlaygroundViewState>) => {
    const current = playgroundTabStateRef.current;
    const nextState: PlaygroundTabState = {
      ...current,
      viewState: {
        ...(current.viewState || DEFAULT_PLAYGROUND_VIEW_STATE),
        ...patch,
      },
    };
    playgroundTabStateRef.current = nextState;
    setPlaygroundTabState(nextState);

    if (!shouldPersistState || !isHydratedForCurrentScope || !playgroundInitialized) return;
    const writeEpoch = persistentCache.captureWriteEpoch();
    if (!persistentCache.isWriteAllowed(persistenceWriteScope, writeEpoch)) return;
    const snapshot = serializeState(splitPlaygroundState(nextState));
    lastSavedPlaygroundStateRef.current = snapshot;
    serializedPlaygroundStateRef.current = snapshot;
    safeLocalStorageSet(playgroundStateKey, snapshot);
    safeLocalStorageSet(playgroundStateTimeKey, Date.now().toString());
  }, [
    isHydratedForCurrentScope,
    persistenceWriteScope,
    playgroundInitialized,
    playgroundStateKey,
    playgroundStateTimeKey,
    setPlaygroundTabState,
    shouldPersistState,
  ]);

  React.useEffect(() => {
    if (!shouldPersistState || !isHydratedForCurrentScope) return;
    const flushPlaygroundState = () => {
      const writeEpoch = persistentCache.captureWriteEpoch();
      if (!persistentCache.isWriteAllowed(persistenceWriteScope, writeEpoch)) return;
      const snapshot = serializedPlaygroundStateRef.current;
      lastSavedPlaygroundStateRef.current = snapshot;
      safeLocalStorageSet(playgroundStateKey, snapshot);
      safeLocalStorageSet(playgroundStateTimeKey, Date.now().toString());
    };
    window.addEventListener('pagehide', flushPlaygroundState);
    return () => window.removeEventListener('pagehide', flushPlaygroundState);
  }, [
    isHydratedForCurrentScope,
    persistenceWriteScope,
    playgroundStateKey,
    playgroundStateTimeKey,
    shouldPersistState,
  ]);

  React.useEffect(() => {
    if (!shouldPersistState || !isHydratedForCurrentScope) {
      return;
    }
    const saveSearchState = async () => {
      try {
        const cleanedState = cleanSearchTabState(searchTabState);
        const serializedState = serializeState(cleanedState);
        if (serializedState === lastSavedSearchStateRef.current) {
          return;
        }

        lastSavedSearchStateRef.current = serializedState;
        safeLocalStorageSet(searchStateKey, serializedState);
        safeLocalStorageSet(searchStateTimeKey, Date.now().toString());
      } catch (error) {
        console.warn('Failed to save search state:', error);
      }
    };

    const timeoutId = window.setTimeout(() => {
      void saveSearchState();
    }, 200);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [
    isHydratedForCurrentScope,
    searchTabState,
    searchStateKey,
    searchStateTimeKey,
    shouldPersistState,
  ]);

  React.useEffect(() => {
    if (!shouldPersistState || !isHydratedForCurrentScope) {
      return;
    }
    const writeEpoch = persistentCache.captureWriteEpoch();
    if (!persistentCache.isWriteAllowed(persistenceWriteScope, writeEpoch)) {
      return;
    }

    safeLocalStorageSet(
      automationsStateKey,
      serializeState(buildPersistedAutomationsTabState(automationsTabState)),
    );
    safeLocalStorageSet(automationsStateTimeKey, Date.now().toString());
  }, [
    automationsStateKey,
    automationsStateTimeKey,
    automationsTabState,
    isHydratedForCurrentScope,
    persistenceWriteScope,
    shouldPersistState,
  ]);

  React.useEffect(() => {
    if (!shouldPersistState || !isHydratedForCurrentScope) {
      return;
    }
    const writeEpoch = persistentCache.captureWriteEpoch();
    if (!persistentCache.isWriteAllowed(persistenceWriteScope, writeEpoch)) {
      return;
    }

    const persistableState = normalizeIndexTabStateForPersistence(indexTabState);
    safeLocalStorageSet(indexStateKey, serializeState(persistableState));
    safeLocalStorageSet(indexStateTimeKey, Date.now().toString());
  }, [
    indexStateKey,
    indexStateTimeKey,
    indexTabState,
    isHydratedForCurrentScope,
    persistenceWriteScope,
    shouldPersistState,
  ]);

  React.useEffect(() => {
    if (!shouldPersistState || !isHydratedForCurrentScope) {
      return;
    }

    // Skip if not initialized to avoid saving default state
    if (!playgroundInitialized) return;
    const writeEpoch = persistentCache.captureWriteEpoch();
    if (!persistentCache.isWriteAllowed(persistenceWriteScope, writeEpoch)) {
      return;
    }

    if (serializedPlaygroundState === lastSavedPlaygroundStateRef.current) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      lastSavedPlaygroundStateRef.current = serializedPlaygroundState;
      safeLocalStorageSet(playgroundStateKey, serializedPlaygroundState);
      safeLocalStorageSet(playgroundStateTimeKey, Date.now().toString());
    }, 200);

    return () => window.clearTimeout(timeoutId);
  }, [
    playgroundInitialized,
    playgroundStateKey,
    playgroundStateTimeKey,
    serializedPlaygroundState,
    isHydratedForCurrentScope,
    persistenceWriteScope,
    shouldPersistState,
  ]);

  React.useEffect(() => {
    if (
      !shouldPersistState
      || !isHydratedForCurrentScope
      || !enginesInitialized
    ) {
      return;
    }

    const writeEpoch = persistentCache.captureWriteEpoch();
    const timeoutId = window.setTimeout(() => {
      if (!persistentCache.isWriteAllowed(persistenceWriteScope, writeEpoch)) {
        return;
      }
      void persistentCache.setMetadata(
        enginesStateKey,
        buildPersistedEnginesTabState(enginesTabState),
        persistenceWriteScope,
      );
    }, 200);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [
    enginesInitialized,
    enginesStateKey,
    enginesTabState,
    isHydratedForCurrentScope,
    persistenceWriteScope,
    shouldPersistState,
  ]);

  const resetTabState = async (tab: 'search' | 'index' | 'playground' | 'engines' | 'automations') => {
    switch (tab) {
      case 'search':
        setSearchTabState(defaultSearchTabState);
        safeLocalStorageRemove(searchStateKey);
        safeLocalStorageRemove(searchStateTimeKey);
        await persistentCache.clearSearchImagePayloads(persistenceScopeId).catch(err =>
          console.warn('Failed to clear search cache data from IndexedDB:', err)
        );
        break;
      case 'index':
        setIndexTabState(defaultIndexTabState);
        safeLocalStorageRemove(indexStateKey);
        safeLocalStorageRemove(indexStateTimeKey);
        break;
      case 'playground':
        setPlaygroundTabState(defaultPlaygroundTabState);
        safeLocalStorageRemove(playgroundStateKey);
        safeLocalStorageRemove(playgroundStateTimeKey);
        // Also clear IndexedDB data
        await persistentCache.setMetadata(
          playgroundHeavyDataKey,
          null,
          persistenceWriteScope,
        ).catch(err =>
          console.warn('Failed to clear playground data from IndexedDB:', err)
        );
        break;
      case 'engines':
        setEnginesTabState(defaultEnginesTabState);
        await persistentCache.setMetadata(
          enginesStateKey,
          buildPersistedEnginesTabState(defaultEnginesTabState),
          persistenceWriteScope,
        ).catch(err =>
          console.warn('Failed to clear extraction engine state from IndexedDB:', err)
        );
        break;
      case 'automations':
        setAutomationsTabState(defaultAutomationsTabState);
        safeLocalStorageRemove(automationsStateKey);
        safeLocalStorageRemove(automationsStateTimeKey);
        break;
    }
  };

  return (
    <TabStateContext.Provider
      value={{
        searchTabState: isHydratedForCurrentScope
          ? searchTabState
          : defaultSearchTabState,
        setSearchTabState,
        indexTabState: isHydratedForCurrentScope
          ? indexTabState
          : defaultIndexTabState,
        setIndexTabState,
        playgroundTabState: isHydratedForCurrentScope
          ? playgroundTabState
          : defaultPlaygroundTabState,
        setPlaygroundTabState,
        commitPlaygroundViewState,
        enginesTabState: isHydratedForCurrentScope
          ? enginesTabState
          : defaultEnginesTabState,
        setEnginesTabState,
        automationsTabState: isHydratedForCurrentScope
          ? automationsTabState
          : defaultAutomationsTabState,
        setAutomationsTabState,
        playgroundTabStateReady: isHydratedForCurrentScope && playgroundInitialized,
        enginesTabStateReady: isHydratedForCurrentScope && enginesInitialized,
        automationsTabStateReady: isHydratedForCurrentScope,
        resetTabState,
      }}
    >
      {children}
    </TabStateContext.Provider>
  );
};

export const useTabState = () => {
  const context = useContext(TabStateContext);
  if (!context) {
    throw new Error('useTabState must be used within a TabStateProvider');
  }
  return context;
};
